import { useState, useMemo, useEffect, useCallback } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import { usePagination } from "../../../../hooks/usePagination";
import { db } from "../../../../services/firebase";
import { collection, getDocs } from "firebase/firestore";
import { COLLECTIONS } from "../../../../services/collections";
import { toast } from "react-toastify";
import CashierShiftService from "../../../../services/cashier_shift_service";
import { FINANCIAL_STATUS, FINANCIAL_TYPES } from "../../../../constants/financial";

const today = () => new Date().toISOString().split("T")[0];

/**
 * useCashier
 * Hook para a tela de Caixa Diário.
 */
export const useCashier = () => {
    const { idTenant, user } = useTenant();

    const [selectedDate, setSelectedDate] = useState(today());
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loadingBanks, setLoadingBanks] = useState(false);

    // Turno de Caixa
    const [currentShift, setCurrentShift] = useState(null);
    const [isProcessingShift, setIsProcessingShift] = useState(false);
    const [modals, setModals] = useState({ open: false, close: false });

    // Modals Control
    const toggleOpenModal = useCallback(() => setModals(prev => ({ ...prev, open: !prev.open })), []);
    const toggleCloseModal = useCallback(() => setModals(prev => ({ ...prev, close: !prev.close })), []);

    // 1. Carregar contas bancárias
    useEffect(() => {
        if (!idTenant) return;

        let isMounted = true;
        const fetchAccounts = async () => {
            setLoadingBanks(true);
            try {
                const snap = await getDocs(
                    collection(db, "tenants", idTenant, COLLECTIONS.BANK_ACCOUNTS)
                );

                if (!isMounted) return;

                const accounts = [];
                snap.forEach(doc => {
                    accounts.push({ id: doc.id, ...doc.data() });
                });
                setBankAccounts(accounts);
            } catch (err) {
                console.error("[useCashier] Erro carregando contas bancárias:", err);
            } finally {
                if (isMounted) setLoadingBanks(false);
            }
        };

        fetchAccounts();
        return () => { isMounted = false; };
    }, [idTenant]);



    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            if (!idTenant) return;
            try {
                const shift = await CashierShiftService.getCurrentOpenShift(idTenant);
                if (isMounted) setCurrentShift(shift);
            } catch (err) {
                console.error("[useCashier] Erro carregando turno:", err);
            }
        };
        load();
        return () => { isMounted = false; };
    }, [idTenant]);

    // 2. Filtros para financialRecords
    const firestoreFilters = useMemo(() => {
        if (currentShift?.id) {
            return [
                { field: "idCashierShift", operator: "==", value: currentShift.id }
            ];
        }
        return [
            { field: "status", operator: "==", value: FINANCIAL_STATUS.PAID },
            { field: "paymentDate", operator: "==", value: selectedDate },
        ];
    }, [selectedDate, currentShift]);

    // Paginação
    const {
        data: backendRecords,
        loading,
        error,
        refresh,
    } = usePagination(
        `tenants/${idTenant}/${COLLECTIONS.FINANCIAL_RECORDS}`,
        idTenant,
        {
            pageSize: 500,
            orderField: currentShift?.id ? "idCashierShift" : "paymentDate",
            orderDirection: "desc",
            filters: firestoreFilters,
        }
    );

    // Ordena localmente
    const records = useMemo(() => {
        if (!backendRecords || backendRecords.length === 0) return [];
        return [...backendRecords].sort((a, b) => {
            const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return timeB - timeA;
        });
    }, [backendRecords]);

    // 3. Resumo
    const summary = useMemo(() => {
        let totalIncome = 0;
        let totalExpense = 0;

        records.forEach(r => {
            if (r.type === FINANCIAL_TYPES.INCOME) totalIncome += r.netAmount || 0;
            if (r.type === FINANCIAL_TYPES.EXPENSE) totalExpense += r.netAmount || 0;
        });

        return {
            totalIncome,
            totalExpense,
            balanceOfDay: totalIncome - totalExpense,
        };
    }, [records]);

    // Ações de Caixa
    const handleOpenShift = useCallback(async ({ idBankAccount, openingBalance }) => {
        try {
            setIsProcessingShift(true);
            const shift = await CashierShiftService.openShift(idTenant, {
                idBankAccount,
                operatorId: user?.uid,
                openingBalance
            });
            setCurrentShift(shift);
            toast.success("Caixa aberto com sucesso!");
            toggleOpenModal();
        } catch (err) {
            console.error("Erro ao abrir caixa:", err);
            toast.error(err.message || "Não foi possível abrir o caixa.");
        } finally {
            setIsProcessingShift(false);
        }
    }, [idTenant, user, toggleOpenModal]);

    const handleCloseShift = useCallback(async ({ closingBalance }) => {
        if (!currentShift?.id) return;
        try {
            setIsProcessingShift(true);
            const systemComputedBalance = currentShift.openingBalance + summary.totalIncome - summary.totalExpense;

            await CashierShiftService.closeShift(idTenant, currentShift.id, {
                closingBalance,
                systemComputedBalance
            });
            setCurrentShift(null);
            toast.success("Caixa fechado com sucesso!");
            toggleCloseModal();
            refresh();
        } catch (err) {
            console.error("Erro ao fechar caixa:", err);
            toast.error(err.message || "Não foi possível fechar o caixa.");
        } finally {
            setIsProcessingShift(false);
        }
    }, [idTenant, currentShift, summary, toggleCloseModal, refresh]);

    return {
        selectedDate,
        setSelectedDate,
        bankAccounts,
        loadingBanks,
        records,
        summary,
        loading,
        error,
        refresh,

        currentShift,
        modals,
        toggleOpenModal,
        toggleCloseModal,
        isProcessingShift,
        handleOpenShift,
        handleCloseShift
    };
};
